import {AfterViewInit, Component, computed, inject, signal, viewChild} from '@angular/core';
import {SelectComponent} from '../../../../shared/components/select/select.component';
import {LookupStore} from '../../../../shared/store/lookup.store';
import {LookupTabBase} from '../../lookup-tab-base';
import {ButtonComponent} from '../../../../shared/components/button/button.component';
import {IVehicleFitment} from '../../../../types/IVehicleFitment';
import {FormsModule} from '@angular/forms';
import {Form} from '../../../../shared/components/form/form';
import {PostHogService} from '../../../../shared/services/posthog.service';
import {
  SelectWithButtonComponent
} from '../../../../shared/components/select/select-with-button/select-with-button.component';
import {IconsComponent} from '../../../../shared/components/icons/icons.component';
import {Modal} from '../../../../shared/components/modal/modal';
import {AlternateFitmentsModal} from './alternate-fitments-modal/alternate-fitments-modal';
import {IAlternateVehicleFitment} from '../../../../types/IAlternateVehicleFitment';

@Component({
  selector: 'app-lookup-by-vehicle',
  imports: [
    SelectComponent,
    ButtonComponent,
    FormsModule,
    Form,
    SelectWithButtonComponent,
    IconsComponent,
    AlternateFitmentsModal
  ],
  templateUrl: './lookup-by-vehicle.html',
  styleUrl: './lookup-by-vehicle.css'
})
export class LookupByVehicle extends LookupTabBase implements AfterViewInit {
  public lookupStore = inject(LookupStore);
  private posthogService = inject(PostHogService);

  alternateSizesModalOpen = signal(false);

  years = Array.from(
    { length: new Date().getFullYear() - 1945 + 1 },
    (_, i) => {
      const year = new Date().getFullYear() - i;
      return { label: year, value: year };
    }
  );

  yearSelect = viewChild.required<SelectComponent>('yearSelect');
  makeSelect = viewChild.required<SelectComponent>('makeSelect');
  modelSelect = viewChild.required<SelectComponent>('modelSelect');
  submodelSelect = viewChild.required<SelectComponent>('submodelSelect');
  fitmentSelect = viewChild.required<SelectComponent>('fitmentSelect');

  ngAfterViewInit() {
    this.yearSelect().focus(100);
  }

  makes = computed(() => this.lookupStore.vehicle.makes()?.map(make => ({label: make, value: make})) ?? []);
  models = computed(() => this.lookupStore.vehicle.models()?.map(model => ({label: model, value: model})) ?? []);
  submodels = computed(() => this.lookupStore.vehicle.submodels()?.map(submodel => ({ label: submodel, value: submodel})) ?? []);
  fitments = computed(() => {
    const seen = new Map<string, { label: string; value: string; position: 'front' | 'rear' | 'both'; fitment: IVehicleFitment }>();

    this.lookupStore.vehicle.fitments()?.forEach((f: IVehicleFitment) => {
      const isStaggered = f.rear_tire_size && f.front_tire_size !== f.rear_tire_size;

      if (isStaggered) {
        const frontKey = f.front_tire_rawsize + '_front';
        const rearKey = f.rear_tire_rawsize + '_rear';

        if (!seen.has(frontKey) || f.fg_chassis_id) {
          seen.set(frontKey, { label: f.front_tire_size, value: frontKey, position: 'front', fitment: f });
        }
        if (!seen.has(rearKey) || f.fg_chassis_id) {
          seen.set(rearKey, { label: f.rear_tire_size, value: rearKey, position: 'rear', fitment: f });
        }
      } else {
        const key = f.front_tire_rawsize + '_both';
        if (!seen.has(key) || f.fg_chassis_id) {
          seen.set(key, { label: f.front_tire_size, value: key, position: 'both', fitment: f });
        }
      }
    });

    return Array.from(seen.values());
  });

  makesDisabled = computed(() => !this.lookupStore.vehicle.selected_year());
  modelsDisabled = computed(() => !this.lookupStore.vehicle.selected_make());
  submodelsDisabled = computed(() => !this.lookupStore.vehicle.selected_model());
  fitmentsDisabled = computed(() => !this.lookupStore.vehicle.selected_submodel());

  isValid = computed(() => {
    const vehicle = this.lookupStore.vehicle();
    return !!(vehicle.selected_year && vehicle.selected_make && vehicle.selected_model && vehicle.selected_submodel && vehicle.selected_fitment);
  });

  onYearChange(year: string) {
    this.makeSelect().focus();
    this.lookupStore.setYear(year);
  }

  onMakeChange(make: string) {
    this.modelSelect().focus();
    this.lookupStore.setMake(make);
  }

  onModelChange(model: string) {
    this.submodelSelect().focus();
    this.lookupStore.setModel(model);
  }

  onSubmodelChange(submodel: string) {
    this.fitmentSelect().focus();
    this.lookupStore.setSubmodel(submodel);
  }

  onFitmentChange(value: string) {
    const [rawsize, position] = value.split('_') as [string, 'front' | 'rear' | 'both'];
    const option = this.fitments().find(f => f.value === value);
    if (!option) return;

    const size = position === 'rear' ? option.fitment.rear_tire_rawsize : option.fitment.front_tire_rawsize;

    this.lookupStore.setFitment(option.fitment);
    this.lookupStore.setRawsize(size);
  }

  selectedFitmentValue = computed(() => {
    const fitment = this.lookupStore.vehicle.selected_fitment();
    const size = this.lookupStore.vehicle.selected_rawsize();
    if (!fitment || !size) return '';

    const isStaggered = fitment.rear_tire_size && fitment.front_tire_size !== fitment.rear_tire_size;
    if (isStaggered) {
      const position = size === fitment.rear_tire_rawsize ? 'rear' : 'front';
      return `${size}_${position}`;
    }
    return `${size}_both`;
  });

  submit() {
    if (this.isValid()) {
      const vehicle = this.lookupStore.vehicle();
      this.posthogService.posthog.capture('tire_search_by_vehicle', {
        year: vehicle.selected_year,
        make: vehicle.selected_make,
        model: vehicle.selected_model,
        submodel: vehicle.selected_submodel,
        fitment: vehicle.selected_fitment,
      });
      this.search.emit({rawsize: this.lookupStore.vehicle.selected_rawsize() ?? undefined});
    }
  }

  onAlternateSearchSubmit(alternate: IAlternateVehicleFitment) {
    this.lookupStore.setRawsize(alternate.tire_size);
    this.alternateSizesModalOpen.set(false);
    this.submit();
  }
}
